import { ActionCreator } from 'redux';

import { isArrayOf, isDefined, isString } from 'lib/typeguards';
import { ApplicationState } from 'store/store';

import { AdjustmentScheme } from 'components/adjustment-schemes/adjustmentSchemeModel';
import { adjustmentSchemesApi } from 'components/adjustment-schemes/adjustmentSchemesApi';
import { awardEnrichmentApi } from 'components/loyalty/AwardEnrichmentsApi';
import { IAwardEnrichment } from 'components/loyalty/models/AwardEnrichment';
import { isPerk, Perk } from 'components/loyalty/models/PerkModel';
import { perkApi } from 'components/loyalty/PerkApi';
import { MatchCodesActionType } from './matchCodesActionTypes';

interface IBaseReducerAction {
    type: MatchCodesActionType;
}

interface IUnknownAction extends IBaseReducerAction {
    type: MatchCodesActionType.UNKNOWN;
}

interface IProgressAction extends IBaseReducerAction {
    type: MatchCodesActionType.START_REQUEST;
}

interface ILoadAction extends IBaseReducerAction {
    type: MatchCodesActionType.LOAD_SUCCESS | MatchCodesActionType.LOAD_ERROR;
    matchCodes?: Record<string, string>;
}

interface IResetCacheAction extends IBaseReducerAction {
    type: MatchCodesActionType.RESET_CACHE;
}

export type IMatchCodesAction = IUnknownAction | IProgressAction | ILoadAction | IResetCacheAction;

const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

export function getAllMatchCodes() {
    return async function (
        dispatch: ActionCreator<ILoadAction | IProgressAction>,
        getState: () => ApplicationState
    ) {
        const state = getState();
        const { lastFetchedAt, matchCodes } = state.matchCodes;

        // If we have recent data, return early
        if (lastFetchedAt && matchCodes && Object.keys(matchCodes).length > 0) {
            const now = new Date().getTime();
            if (now - lastFetchedAt < CACHE_DURATION) {
                return;
            }
        }

        dispatch({ type: MatchCodesActionType.START_REQUEST });
        try {
            const pepperPerksResponse = await perkApi.getList();
            const awardEnrichmentsResponse = await awardEnrichmentApi.getAllAwardEnrichments();
            const adjustmentSchemesResponse = await adjustmentSchemesApi.getAllSchemes();

            if (!pepperPerksResponse.ok && !awardEnrichmentsResponse.ok && !adjustmentSchemesResponse.ok) {
                throw new Error('Failed to load match codes');
            }

            const perks: Perk[] = pepperPerksResponse.body.perks;
            const awardEnrichments: IAwardEnrichment[] = awardEnrichmentsResponse.items;
            const adjustmentSchemes: AdjustmentScheme[] = adjustmentSchemesResponse.items;

            let matchCodes: Record<string, string> = {};

            matchCodes = [...perks, ...awardEnrichments, ...adjustmentSchemes].reduce<Record<string, string>>(
                (acc, perk) => {
                    const scopeMatchCode = perk.discount?.scopeMatchCode;
                    const matchCode = perk.basketQualification?.matchCode;
                    let title = '';
                    if (isPerk(perk) && perk.internalTitle && perk.internalTitle.trim() !== '') {
                        title = perk.internalTitle;
                    }
                    if (isDefined(perk.title) && perk.title.trim() !== '') {
                        title = perk.title;
                    }
                    if (scopeMatchCode) {
                        if (isArrayOf(isString, scopeMatchCode)) {
                            scopeMatchCode.forEach(code => {
                                acc[code] = `${title} (${code})`;
                            });
                        } else if (isString(scopeMatchCode)) {
                            acc[scopeMatchCode] = `${title} (${scopeMatchCode})`;
                        } else {
                            // Handle grouped scopeMatchCode with the format [[code1, code2], [code3, code4]]
                            scopeMatchCode.forEach(group => {
                                const combinedGroup = group.join(', ');
                                acc[combinedGroup] = `${title} (${combinedGroup})`;
                            });
                        }
                    }
                    if (matchCode) {
                        if (Array.isArray(matchCode)) {
                            matchCode.forEach(code => {
                                acc[code] = `${title} (${code})`;
                            });
                        } else {
                            acc[matchCode] = `${title} (${matchCode})`;
                        }
                    }

                    return acc;
                },
                {}
            );

            dispatch({
                type: MatchCodesActionType.LOAD_SUCCESS,
                matchCodes
            });
        } catch (err) {
            dispatch({
                type: MatchCodesActionType.LOAD_ERROR
            });
        }
    };
}
